import { ref, computed, watch } from "vue";

export interface InfoBannerConfig {
  key: string;
  message: string;
  textColor?: string;
  link?: string;
  linkTarget?: "_blank" | "_self";
  linkText?: string;
  // allows the banner to be re-shown after being dismissed for a certain number of days.
  // If not set, the banner will be dismissed indefinitely in local storage
  dismissForDays?: number;
}

const BANNER_DISMISS_KEY = "infoBannerDismissed";
function getDismissed(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(BANNER_DISMISS_KEY) || "{}");
  } catch {
    return {};
  }
}
function setDismissed(map: Record<string, number>) {
  localStorage.setItem(BANNER_DISMISS_KEY, JSON.stringify(map));
}

const banners = ref<InfoBannerConfig[]>([]);
const dismissed = ref<Record<string, number>>(getDismissed());
const hovered = ref(false);
const currentIdx = ref(0);
let timer: number | null = null;

function addBanner(config: InfoBannerConfig) {
  banners.value.push(config);
}

function removeBanner(key: string) {
  // Remove all banners with the same key
  banners.value = banners.value.filter((b) => b.key !== key);
}

function dismissBanner(key: string) {
  const now = Date.now();
  // Dismiss all banners with the same key
  banners.value.forEach((b) => {
    if (b.key === key) dismissed.value[b.key] = now;
  });
  setDismissed(dismissed.value);
  removeBanner(key);
}

function isDismissed(banner: InfoBannerConfig) {
  const ts = dismissed.value[banner.key];
  if (!ts) return false;
  if (banner.dismissForDays) {
    const ms = banner.dismissForDays * 86400000;
    return now() - ts < ms;
  }
  return true;
}

function now() {
  return Date.now();
}

const visibleBanners = computed(() =>
  banners.value.filter((b) => !isDismissed(b)),
);

const currentBanner = computed(() =>
  visibleBanners.value.length
    ? visibleBanners.value[currentIdx.value % visibleBanners.value.length]
    : null,
);

function nextBanner() {
  if (visibleBanners.value.length > 1)
    currentIdx.value = (currentIdx.value + 1) % visibleBanners.value.length;
}

function startRotation() {
  stopRotation();
  if (visibleBanners.value.length > 1 && !hovered.value)
    timer = window.setInterval(nextBanner, 8000);
}

function stopRotation() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

watch([visibleBanners, hovered], startRotation, { immediate: true });

export function useInfoBanner() {
  return {
    banners,
    addBanner,
    removeBanner,
    dismissBanner,
    currentBanner,
    hovered,
    startRotation,
    stopRotation,
  };
}
